import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { catalogDistributorServices, ServiceConnectionStatus } from 'fym-shared/src/smartlinks/catalog';
import { ServiceConnection } from '../../../redux/api/fymAPI.types';
import { isArtistPortalReachable } from './artistPortalCardState';

/**
 * The service strip's head line and its two sections.
 *
 * design: connector-waitlist a1 — "{connected} of {total} connected", music services first, then
 * distributors. The web has no waiting list any more, so the head line counts no list.
 */
export function useConnectionsPanelSummary(connections: ServiceConnection[] | undefined) {
  const intl = useIntl();

  /*
   * A service counts once it really answers with the artist's own numbers: a
   * signed-in service that still waits for the artist to pick which artist the
   * login means reads nothing yet.
   */
  const connectedCount = useMemo(
    () =>
      connections?.filter(
        (c) => isArtistPortalReachable(c) || (c.authKind === null && c.status === ServiceConnectionStatus.connected)
      ).length ?? 0,
    [connections]
  );

  // design: connector-waitlist a1 — music services first, then distributors.
  const streamingConnections = useMemo(
    () => connections?.filter((c) => !catalogDistributorServices.includes(c.integrationType)) ?? [],
    [connections]
  );
  const distributorConnections = useMemo(
    () => connections?.filter((c) => catalogDistributorServices.includes(c.integrationType)) ?? [],
    [connections]
  );

  const meta = useMemo(() => {
    if (connections === undefined) {
      return undefined;
    }
    return intl.formatMessage(
      { id: 'app.sml.catalog.connections.count', defaultMessage: '{connected} of {total} connected' },
      { connected: connectedCount, total: connections.length }
    );
  }, [connectedCount, connections, intl]);

  return { meta, streamingConnections, distributorConnections };
}
